/**
 * TMDB, falado direto pelo aparelho.
 *
 * Não passa pelo nosso servidor: o Flutter já faz assim
 * (`lib/features/cinema/data/services/tmdb/`) e não existe rota de TMDB no
 * `ROUTES`. Este arquivo é o espelho dos quatro serviços de lá —
 * `tmdb_base`, `tmdb_discovery_service`, `tmdb_details_service`,
 * `tmdb_search_service` e `tmdb_trailer_service` — com os mesmos parâmetros
 * de consulta, para que as duas telas mostrem a mesma prateleira.
 *
 * Sem `language`: o Flutter também não manda. Título e sinopse saem em
 * inglês nos dois apps, e é o título em inglês que vai gravado na
 * `watch_list` que os dois leem — mandar `pt-BR` só aqui faria o mesmo filme
 * ter dois nomes conforme o aparelho que o salvou.
 */

const BASE = 'https://api.themoviedb.org/3';
const IMG = 'https://image.tmdb.org/t/p/w500';
const IMG_ORIGINAL = 'https://image.tmdb.org/t/p/original';
const IMG_PERFIL = 'https://image.tmdb.org/t/p/w185';

/**
 * A chave que o app usa quando não vem outra. É a mesma do `EnvConfig` do
 * Flutter (`lib/core/config/env_config.dart`): uma chave de leitura v3, sem
 * escopo de conta, que o projeto de origem já serve publicamente. Não é
 * segredo — é o que faz o cinema funcionar sem configuração. Trocar é uma
 * linha no `.env` do site (`VITE_TMDB_KEY`).
 */
export const CHAVE_TMDB_PADRAO = 'b41bd33efc365bbdbbad2e31dae8f573';

export const imagemTMDB = (caminho?: string | null): string =>
  caminho ? `${IMG}${caminho}` : '';
export const fundoTMDB = (caminho?: string | null): string =>
  caminho ? `${IMG_ORIGINAL}${caminho}` : '';
export const perfilTMDB = (caminho?: string | null): string =>
  caminho ? `${IMG_PERFIL}${caminho}` : '';

export type TipoMidia = 'movie' | 'tv';

/** O que a tela usa. Os campos crus do TMDB ficam em `bruto`. */
export interface ItemMidia {
  tmdbId: number;
  titulo: string;
  tipo: TipoMidia;
  /** URL inteira, como o `mapResultToMediaItem` do Flutter grava. */
  poster: string;
  fundo: string;
  ano: string;
  sinopse: string;
  nota: number;
  /** Animação japonesa. A tela de cinema tira estes da prateleira. */
  anime: boolean;
  popularidade: number;
  bruto: Record<string, unknown>;
}

export interface ResultadoTMDB {
  itens: ItemMidia[];
  pagina: number;
  totalPaginas: number;
}

export interface PessoaDoElenco {
  id: number;
  nome: string;
  personagem: string;
  foto: string;
}

export interface CriticaTMDB {
  id: string;
  autor: string;
  texto: string;
  nota: number | null;
  data: string;
  avatar: string;
}

export interface EpisodioTMDB {
  numero: number;
  temporada: number;
  nome: string;
  resumo: string;
  imagem: string;
  duracao: number | null;
  nota: number;
  estreia: string;
}

export interface OpcoesDescoberta {
  tipo: TipoMidia;
  ordem?: string;
  generos?: number[];
  anoDe?: number;
  anoAte?: number;
  notaMinima?: number;
  votosMinimos?: number;
  idiomaOriginal?: string;
  pagina?: number;
}

/**
 * Animação japonesa, do jeito que o `detectAnime` do Flutter decide: idioma
 * original japonês **e** o gênero 16 (Animação). As listas trazem
 * `genre_ids`, o detalhe traz `genres`; as duas formas contam.
 */
export function ehAnime(bruto: Record<string, any>): boolean {
  if ((bruto.original_language ?? '') !== 'ja') return false;
  const ids = bruto.genre_ids;
  if (Array.isArray(ids) && ids.some((g: unknown) => Number(g) === 16)) return true;
  const generos = bruto.genres;
  if (Array.isArray(generos) && generos.some((g: any) => Number(g?.id) === 16)) return true;
  const palavras = bruto.keywords?.results;
  if (Array.isArray(palavras)) {
    return palavras.some((k: any) => String(k?.name ?? '').toLowerCase() === 'anime');
  }
  return false;
}

function mapear(bruto: Record<string, any>, tipoForcado?: TipoMidia): ItemMidia {
  const tipo: TipoMidia = tipoForcado ?? (bruto.media_type === 'tv' ? 'tv' : 'movie');
  // O Flutter escolhe o campo pelo tipo e só então cai no outro: uma série
  // que venha com `title` preenchido continua sendo o `name` que aparece.
  const titulo =
    tipo === 'movie'
      ? bruto.title || bruto.name || 'Sem título'
      : bruto.name || bruto.title || 'Sem título';
  const data: string = bruto.release_date || bruto.first_air_date || '';
  return {
    tmdbId: Number(bruto.id ?? 0),
    titulo,
    tipo,
    poster: imagemTMDB(bruto.poster_path),
    fundo: fundoTMDB(bruto.backdrop_path),
    ano: data.length >= 4 ? data.slice(0, 4) : '',
    sinopse: bruto.overview || '',
    nota: Number(bruto.vote_average ?? 0),
    anime: ehAnime(bruto),
    popularidade: Number(bruto.popularity ?? 0),
    bruto,
  };
}

export class TMDB {
  /** `tipo_id` -> chave do YouTube, como o `_trailerCache` do Flutter. */
  private readonly trailers = new Map<string, string | null>();

  /** `tipo/id` -> id do IMDb, como o `_imdbCache` do `TorrentService`. */
  private readonly imdbs = new Map<string, string>();

  constructor(
    private readonly chave: string,
    private readonly timeout = 15_000,
  ) {}

  private async buscar(caminho: string, params: Record<string, string> = {}): Promise<any> {
    if (!this.chave) throw new Error('Falta a chave do TMDB nos ajustes.');
    const controle = new AbortController();
    const prazo = setTimeout(() => controle.abort(), this.timeout);
    try {
      const q = new URLSearchParams({ api_key: this.chave, ...params });
      const r = await fetch(`${BASE}${caminho}?${q}`, { signal: controle.signal });
      if (!r.ok) throw new Error(`TMDB respondeu ${r.status}.`);
      return await r.json();
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        throw new Error('O TMDB demorou demais.');
      }
      throw err;
    } finally {
      clearTimeout(prazo);
    }
  }

  /**
   * O catálogo não derruba a tela: uma prateleira que falhou volta vazia e
   * some, como no Flutter, onde cada `fetch*` engole o erro e devolve `[]`.
   * A busca, essa sim, deixa o erro subir — quem digitou precisa saber.
   */
  private async lista(
    caminho: string,
    params: Record<string, string>,
    tipoForcado?: TipoMidia,
  ): Promise<ItemMidia[]> {
    try {
      const d = await this.buscar(caminho, params);
      return (d.results ?? []).map((x: any) => mapear(x, tipoForcado));
    } catch {
      return [];
    }
  }

  // ── Catálogo ────────────────────────────────────────────────────────

  /**
   * Em alta. O `/trending` do TMDB é global e não aceita região: com uma
   * região de verdade o Flutter desvia para o feed do país, e aqui também.
   */
  async emAlta(regiao = 'all', janela: 'day' | 'week' = 'week'): Promise<ItemMidia[]> {
    if (regiao && regiao !== 'all') return this.emAltaNoPais(regiao);
    try {
      const d = await this.buscar(`/trending/all/${janela}`);
      return (d.results ?? [])
        .filter((x: any) => x.media_type === 'movie' || x.media_type === 'tv')
        .map((x: any) => mapear(x));
    } catch {
      return [];
    }
  }

  /**
   * O "top 10 do país": o que é popular **e** dá para assistir por lá. Sem
   * `with_origin_country` de propósito — aquilo devolve só produção local.
   */
  async emAltaNoPais(pais: string): Promise<ItemMidia[]> {
    const cc = pais.toUpperCase();
    const comum = {
      sort_by: 'popularity.desc',
      watch_region: cc,
      with_watch_monetization_types: 'flatrate|free|ads',
      include_adult: 'false',
      'vote_count.gte': '50',
      page: '1',
    };
    const [filmes, series] = await Promise.all([
      this.lista('/discover/movie', { ...comum, region: cc }, 'movie'),
      this.lista('/discover/tv', comum, 'tv'),
    ]);
    return [...filmes, ...series].sort((a, b) => b.popularidade - a.popularidade);
  }

  filmesMaisBemAvaliados(): Promise<ItemMidia[]> {
    return this.lista('/movie/top_rated', {}, 'movie');
  }

  filmesPopulares(): Promise<ItemMidia[]> {
    return this.lista('/movie/popular', {}, 'movie');
  }

  seriesPopulares(): Promise<ItemMidia[]> {
    return this.lista('/tv/popular', {}, 'tv');
  }

  seriesMaisBemAvaliadas(): Promise<ItemMidia[]> {
    return this.lista('/tv/top_rated', {}, 'tv');
  }

  noArHoje(): Promise<ItemMidia[]> {
    return this.lista('/tv/airing_today', {}, 'tv');
  }

  noArAgora(): Promise<ItemMidia[]> {
    return this.lista('/tv/on_the_air', {}, 'tv');
  }

  emCartaz(regiao = 'BR'): Promise<ItemMidia[]> {
    return this.lista('/movie/now_playing', { region: regiao }, 'movie');
  }

  emBreve(regiao = 'BR'): Promise<ItemMidia[]> {
    return this.lista('/movie/upcoming', { region: regiao }, 'movie');
  }

  /** Os gêneros do TMDB, por id. */
  async generos(tipo: TipoMidia): Promise<Record<number, string>> {
    try {
      const d = await this.buscar(`/genre/${tipo}/list`);
      const mapa: Record<number, string> = {};
      for (const g of d.genres ?? []) mapa[Number(g.id)] = String(g.name);
      return mapa;
    } catch {
      return {};
    }
  }

  /** Um gênero só. Sem página: o Flutter também não pagina aqui. */
  porGenero(genero: number, tipo: TipoMidia, ordem = 'popularity.desc'): Promise<ItemMidia[]> {
    return this.lista(
      `/discover/${tipo}`,
      { with_genres: String(genero), sort_by: ordem },
      tipo,
    );
  }

  /** A descoberta livre: idioma, década, nota, gênero, ordenação. */
  descobrirMidia(o: OpcoesDescoberta): Promise<ItemMidia[]> {
    const params: Record<string, string> = {
      include_adult: 'false',
      page: String(o.pagina ?? 1),
    };
    if (o.ordem) params.sort_by = o.ordem;
    if (o.generos?.length) params.with_genres = o.generos.join(',');
    if (o.notaMinima != null) params['vote_average.gte'] = String(o.notaMinima);
    if (o.votosMinimos != null) params['vote_count.gte'] = String(o.votosMinimos);
    if (o.idiomaOriginal) params.with_original_language = o.idiomaOriginal;
    // Série e filme guardam a data de estreia em campos diferentes.
    const campo = o.tipo === 'tv' ? 'first_air_date' : 'primary_release_date';
    if (o.anoDe != null) params[`${campo}.gte`] = `${o.anoDe}-01-01`;
    if (o.anoAte != null) params[`${campo}.lte`] = `${o.anoAte}-12-31`;
    return this.lista(`/discover/${o.tipo}`, params, o.tipo);
  }

  async descobrir(tipo: TipoMidia = 'movie', pagina = 1): Promise<ResultadoTMDB> {
    const d = await this.buscar(`/discover/${tipo}`, {
      page: String(pagina),
      sort_by: 'popularity.desc',
    });
    return {
      itens: (d.results ?? []).map((x: any) => mapear(x, tipo)),
      pagina: d.page ?? pagina,
      totalPaginas: d.total_pages ?? 1,
    };
  }

  // ── Busca ───────────────────────────────────────────────────────────

  async procurar(termo: string, pagina = 1): Promise<ResultadoTMDB> {
    const d = await this.buscar('/search/multi', {
      query: termo,
      page: String(pagina),
      include_adult: 'false',
    });
    const itens = (d.results ?? [])
      // `multi` traz pessoas junto, e pessoa não é card de cinema.
      .filter((x: any) => x.media_type === 'movie' || x.media_type === 'tv')
      .map((x: any) => mapear(x));
    return { itens, pagina: d.page ?? pagina, totalPaginas: d.total_pages ?? 1 };
  }

  /** O id de uma série pelo nome. Usado quando só se tem o título. */
  async procurarSerie(titulo: string, ano?: string): Promise<number | null> {
    try {
      const params: Record<string, string> = { query: titulo };
      if (ano) params.first_air_date_year = ano;
      const d = await this.buscar('/search/tv', params);
      const primeiro = (d.results ?? [])[0];
      return primeiro ? Number(primeiro.id) : null;
    } catch {
      return null;
    }
  }

  // ── Detalhe ─────────────────────────────────────────────────────────

  async detalhes(id: number, tipo: TipoMidia): Promise<Record<string, any> | null> {
    try {
      return await this.buscar(`/${tipo}/${id}`);
    } catch {
      return null;
    }
  }

  /** Os primeiros quinze do elenco, como no Flutter. */
  async elenco(id: number, tipo: TipoMidia): Promise<PessoaDoElenco[]> {
    try {
      const d = await this.buscar(`/${tipo}/${id}/credits`);
      return ((d.cast ?? []) as any[]).slice(0, 15).map((c) => ({
        id: Number(c.id ?? 0),
        nome: c.name ?? 'Unknown',
        personagem: c.character ?? '',
        foto: perfilTMDB(c.profile_path),
      }));
    } catch {
      return [];
    }
  }

  /** As oito primeiras críticas. */
  async criticas(id: number, tipo: TipoMidia): Promise<CriticaTMDB[]> {
    try {
      const d = await this.buscar(`/${tipo}/${id}/reviews`);
      return ((d.results ?? []) as any[]).slice(0, 8).map((r) => ({
        id: String(r.id ?? ''),
        autor: r.author ?? 'Anonymous',
        texto: r.content ?? '',
        nota: r.author_details?.rating != null ? Number(r.author_details.rating) : null,
        data: r.created_at ?? '',
        avatar: perfilTMDB(r.author_details?.avatar_path),
      }));
    } catch {
      return [];
    }
  }

  parecidos(id: number, tipo: TipoMidia): Promise<ItemMidia[]> {
    return this.lista(`/${tipo}/${id}/similar`, {}, tipo);
  }

  /** Os episódios de uma temporada. */
  async episodios(serie: number, temporada: number): Promise<EpisodioTMDB[]> {
    try {
      const d = await this.buscar(`/tv/${serie}/season/${temporada}`);
      return ((d.episodes ?? []) as any[]).map((e) => ({
        numero: Number(e.episode_number ?? 0),
        temporada: Number(e.season_number ?? temporada),
        nome: e.name ?? '',
        resumo: e.overview ?? '',
        imagem: imagemTMDB(e.still_path),
        duracao: e.runtime != null ? Number(e.runtime) : null,
        nota: Number(e.vote_average ?? 0),
        estreia: e.air_date ?? '',
      }));
    } catch {
      return [];
    }
  }

  /**
   * A chave do trailer no YouTube. A ordem é a do Flutter: trailer oficial,
   * depois qualquer trailer, depois qualquer vídeo do YouTube.
   */
  async trailer(id: number, tipo: TipoMidia): Promise<string | null> {
    const cache = `${tipo}_${id}`;
    if (this.trailers.has(cache)) return this.trailers.get(cache) ?? null;
    let chave: string | null = null;
    try {
      const d = await this.buscar(`/${tipo}/${id}/videos`);
      const videos: any[] = d.results ?? [];
      const achado =
        videos.find((v) => v.site === 'YouTube' && v.type === 'Trailer' && v.official === true) ??
        videos.find((v) => v.site === 'YouTube' && v.type === 'Trailer') ??
        videos.find((v) => v.site === 'YouTube');
      chave = achado?.key ?? null;
    } catch {
      chave = null;
    }
    this.trailers.set(cache, chave);
    return chave;
  }

  /**
   * O id do IMDb (`tt1160419`) para um id do TMDB, ou nulo quando o TMDB não
   * tem o cruzamento — o que acontece com título muito novo ou muito obscuro.
   *
   * Porte de `TorrentService.imdbId`. Guardado em memória com a mesma chave
   * (`tipo/id`) porque o cruzamento nunca muda e a consulta custa uma volta.
   */
  async imdbDe(id: number, tipo: TipoMidia): Promise<string | null> {
    const cache = `${tipo}/${id}`;
    const guardado = this.imdbs.get(cache);
    if (guardado !== undefined) return guardado;
    let achado: string | null = null;
    try {
      const d = await this.buscar(`/${tipo}/${id}/external_ids`);
      const imdb = d?.imdb_id;
      achado = typeof imdb === 'string' && imdb !== '' ? imdb : null;
    } catch {
      achado = null;
    }
    /* Só o achado fica guardado: uma falha de rede não deve condenar o título
       a nunca mais ter torrent nesta sessão. */
    if (achado !== null) this.imdbs.set(cache, achado);
    return achado;
  }
}
